import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCourseApi,
  deleteCourseApi,
  getCourseApi,
  getCoursesApi,
  updateCourseApi,
} from "../api/coursesApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { CourseId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateCourse,
  RestUpdateCourse,
} from "@KassaroAB/shared/types/course";

const coursesQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "courses");

const invalidateCourseQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: coursesQueryKey(scope) });
};

export const useGetCoursesQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: coursesQueryKey(scope),
    queryFn: () => getCoursesApi(scope!),
    enabled: !!scope,
  });
};

export const useGetCourseQuery = (
  scope: CatalogScope | null,
  courseId: CourseId | null | undefined,
) => {
  return useQuery({
    queryKey: [...coursesQueryKey(scope), courseId],
    queryFn: () => getCourseApi(scope!, courseId!),
    enabled: !!scope && !!courseId,
  });
};

export const useCreateCourseMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (course: RestCreateCourse) =>
      createCourseApi(scope!, course),
    onSuccess: () => {
      invalidateCourseQueries(queryClient, scope);
    },
  });
};

export const useUpdateCourseMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      course,
    }: {
      courseId: CourseId;
      course: RestUpdateCourse;
    }) => updateCourseApi(scope!, courseId, course),
    onSuccess: () => {
      invalidateCourseQueries(queryClient, scope);
    },
  });
};

export const useDeleteCourseMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: CourseId) =>
      deleteCourseApi(scope!, courseId),
    onSuccess: () => {
      invalidateCourseQueries(queryClient, scope);
    },
  });
};
